import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Wrench, Cog, ShieldCheck, Clock, Zap, CheckCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { BUSINESS, MEDIA } from "@/constants/business";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const services = [
  {
    icon: <Building2 className="w-8 h-8" />,
    title: "Installation",
    desc: "Turnkey elevator & escalator installation for residential, commercial, industrial and infrastructure projects.",
    points: [
      "Site survey & structural feasibility",
      "Shaft & pit engineering support",
      "Compliance with IS 14665 & lift acts",
      "Handover with commissioning report",
    ],
  },
  {
    icon: <Wrench className="w-8 h-8" />,
    title: "Maintenance & AMC",
    desc: "Preventive & comprehensive AMC packages with 24×7 breakdown response and safety audits.",
    points: [
      "Scheduled monthly / quarterly checks",
      "Genuine spare replacements",
      "Rope tension & brake calibration",
      "24/7 emergency helpline",
    ],
  },
  {
    icon: <Cog className="w-8 h-8" />,
    title: "Modernization & Repair",
    desc: "Refresh aging lifts with modern controllers, energy-efficient motors, and premium cabin interiors.",
    points: [
      "VVVF drive upgrades",
      "Automatic Rescue Device (ARD)",
      "Interior refit & LED lighting",
      "IoT-enabled monitoring",
    ],
  },
];

export default function ServicesPage() {
  return (
    <>
      <SEO
        title="Elevator Services in Bangalore | Installation, Maintenance & Modernization | Vila"
        description="Vila Elevators offers professional elevator installation, AMC maintenance, and modernization across Karnataka, Andhra Pradesh, Telangana & Tamil Nadu. 24/7 support."
        path="/services"
        keywords="elevator installation bangalore, elevator maintenance, elevator amc, lift modernization, elevator repair karnataka"
      />

      <PageHeader
        eyebrow="Our Services"
        title="End-to-end elevator & escalator services."
        desc="From first drawing to lifetime support — we handle every stage of your vertical transportation journey."
        image={MEDIA.hero}
      />

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-24">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${i % 2 === 1 ? "lg:direction-rtl" : ""}`}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="w-16 h-16 border border-[#b08d57]/40 flex items-center justify-center text-[#b08d57] mb-6">
                  {s.icon}
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-6">
                  {s.title}
                </h2>
                <p className="text-[#6b6b6b] text-lg leading-relaxed mb-8">{s.desc}</p>
                <ul className="space-y-3 mb-8">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-[#4a4a4a]">
                      <CheckCircle className="w-5 h-5 text-[#b08d57] flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.title.toLowerCase().split(" ")[0]}`}
                  className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm transition-colors"
                >
                  Get Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <img
                  src={[MEDIA.productElevator, MEDIA.hero, MEDIA.darkCabin][i]}
                  alt={s.title}
                  className="w-full aspect-[4/5] object-cover border border-[#e5ddd0]"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <FeatureBlock icon={<ShieldCheck className="w-6 h-6" />} title="ISO-grade Safety" desc="Every installation exceeds Indian & international lift safety standards." />
          <FeatureBlock icon={<Clock className="w-6 h-6" />} title="On-time Delivery" desc="Predictable timelines thanks to in-house manufacturing." />
          <FeatureBlock icon={<Zap className="w-6 h-6" />} title="Energy Efficient" desc="Regenerative drives cut electricity bills by up to 40%." />
        </div>
      </section>
    </>
  );
}

export function PageHeader({ eyebrow, title, desc, image }) {
  return (
    <section className="relative pt-32 pb-20 border-b border-[#e5ddd0] overflow-hidden">
      {image && (
        <>
          <img src={image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#faf7f2] via-[#faf7f2]/80 to-[#faf7f2]" />
        </>
      )}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">{eyebrow}</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1a1a1a] max-w-4xl leading-[0.95]">{title}</h1>
        {desc && <p className="text-[#6b6b6b] text-lg max-w-2xl mt-6 leading-relaxed">{desc}</p>}
      </div>
    </section>
  );
}

function FeatureBlock({ icon, title, desc }) {
  return (
    <div>
      <div className="w-12 h-12 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-display text-xl font-bold text-[#1a1a1a] mb-2">{title}</h3>
      <p className="text-[#6b6b6b]">{desc}</p>
    </div>
  );
}
